<template>
  <div class="px-4 sm:px-6 lg:px-0 py-8 max-w-7xl mx-auto">
    <!-- Header -->
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6"
    >
      <div>
        <h1 class="text-2xl sm:text-3xl font-bold text-white mb-1">
          Usuarios administradores
        </h1>
        <p class="text-bochas-text-muted text-sm">
          Gestioná los usuarios con acceso al panel. Los nuevos deben cambiar su
          contraseña al primer ingreso.
        </p>
      </div>
      <button
        @click="showForm = !showForm"
        class="btn-bochas-rojo w-full text-center text-sm whitespace-nowrap sm:w-auto"
      >
        {{ showForm ? "Cancelar" : "+ Crear usuario" }}
      </button>
    </div>

    <!-- Toast de éxito/error -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        :class="[
          'fixed top-4 right-4 z-[100] px-4 py-3 rounded-lg shadow-lg text-sm flex items-center gap-2 max-w-sm',
          toast.type === 'success'
            ? 'bg-green-500/20 border border-green-500 text-green-200'
            : 'bg-bochas-rojo/20 border border-bochas-rojo text-red-200',
        ]"
      >
        <svg
          v-if="toast.type === 'success'"
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
          />
        </svg>
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- Form de creación (colapsable) -->
    <div v-if="showForm" class="card-fbochas p-6 mb-6">
      <h2 class="text-lg font-semibold text-white mb-4">Nuevo usuario</h2>
      <form @submit.prevent="onCreate" class="space-y-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Nombre</label
            >
            <input
              v-model="form.firstname"
              type="text"
              required
              class="w-full px-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo"
              placeholder="Juan"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Apellido</label
            >
            <input
              v-model="form.lastname"
              type="text"
              required
              class="w-full px-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo"
              placeholder="Pérez"
            />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Email</label
          >
          <input
            v-model="form.email"
            type="email"
            required
            class="w-full px-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo"
            placeholder="usuario@bochascba.com"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2"
            >Rol</label
          >
          <div class="grid gap-2 sm:grid-cols-3">
            <button
              v-for="role in roleOptions"
              :key="role.value"
              type="button"
              class="rounded-lg border px-3 py-3 text-left text-sm font-semibold transition-colors"
              :class="
                form.role === role.value
                  ? 'border-bochas-rojo bg-bochas-rojo/20 text-white'
                  : 'border-gray-700 bg-bochas-bg text-gray-300 hover:border-gray-500 hover:text-white'
              "
              @click="form.role = role.value"
            >
              {{ role.label }}
            </button>
          </div>
        </div>
        <label
          v-if="form.role === ROLES.BOCHAS_ADMIN"
          class="flex items-center gap-3 rounded-lg border border-gray-700 bg-bochas-bg px-4 py-3 text-sm text-gray-300"
        >
          <input
            v-model="form.isDeveloper"
            type="checkbox"
            class="h-4 w-4 rounded border-gray-600 bg-bochas-card-bg text-bochas-rojo focus:ring-bochas-rojo"
          />
          <span>Administrador general</span>
        </label>
        <div
          v-if="needsAsociacion"
          class="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Asociación</label
            >
            <select
              v-model.number="form.asociacionId"
              required
              class="w-full px-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-rojo"
            >
              <option :value="null" disabled>Seleccionar asociación</option>
              <option
                v-for="asociación in asociaciones"
                :key="asociación.id"
                :value="asociación.id"
              >
                {{ asociación.name }}
              </option>
            </select>
          </div>
          <div v-if="form.role === ROLES.CLUB_ADMIN">
            <label class="block text-sm font-medium text-gray-300 mb-2"
              >Club</label
            >
            <select
              v-model.number="form.clubId"
              required
              class="w-full px-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-rojo"
            >
              <option :value="null" disabled>Seleccionar club</option>
              <option
                v-for="club in clubesFiltrados"
                :key="club.id"
                :value="club.id"
              >
                {{ club.name }}
              </option>
            </select>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-300 mb-2">
            Contraseña temporal
            <span class="text-gray-500 text-xs ml-2"
              >(mínimo 6 caracteres)</span
            >
          </label>
          <div class="relative">
            <input
              v-model="form.password"
              :type="showTempPassword ? 'text' : 'password'"
              required
              minlength="6"
              class="w-full px-4 py-2.5 pr-12 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-rojo"
              placeholder="temporal123"
            />
            <button
              type="button"
              @click="showTempPassword = !showTempPassword"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
            >
              <svg
                v-if="showTempPassword"
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            El usuario deberá cambiarla al primer ingreso.
          </p>
        </div>

        <div
          v-if="formError"
          class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
        >
          {{ formError }}
        </div>

        <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            @click="
              showForm = false;
              resetCreateForm();
            "
            class="px-4 py-2.5 text-gray-400 hover:text-white"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="formLoading"
            class="btn-bochas-rojo flex items-center justify-center gap-2"
            :class="{ 'opacity-60 cursor-not-allowed': formLoading }"
          >
            <span v-if="!formLoading">Crear usuario</span>
            <span v-else>Creando...</span>
          </button>
        </div>
      </form>
    </div>

    <!-- Búsqueda + tabla -->
    <div v-if="!loading" class="mb-4">
      <div class="relative">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          v-model="search"
          type="text"
          placeholder="Buscar por nombre o email..."
          class="w-full pl-10 pr-4 py-2.5 bg-bochas-bg border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-bochas-azul"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12">
      <div
        class="inline-block w-10 h-10 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin"
      ></div>
    </div>

    <!-- Lista -->
    <div v-else class="card-fbochas overflow-hidden">
      <table class="w-full">
        <thead class="bg-bochas-bg/50 border-b border-bochas-card-border">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase text-bochas-text-muted"
            >
              Nombre
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase text-bochas-text-muted hidden sm:table-cell"
            >
              Email
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase text-bochas-text-muted hidden lg:table-cell"
            >
              Rol
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase text-bochas-text-muted hidden md:table-cell"
            >
              Estado
            </th>
            <th
              class="px-4 py-3 text-right text-xs font-semibold uppercase text-bochas-text-muted"
            >
              Acciones
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in filteredUsers"
            :key="user.id"
            class="border-b border-bochas-card-border last:border-0 hover:bg-white/5 transition-colors"
          >
            <td class="px-4 py-3 text-white">
              <div class="flex items-center gap-3">
                <div
                  class="w-9 h-9 rounded-full bg-degradado-bochas flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                >
                  {{ initials(user) }}
                </div>
                <div>
                  <div class="font-medium">
                    {{ user.firstname }} {{ user.lastname }}
                    <span
                      v-if="user.id === currentUserId"
                      class="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-bochas-azul/30 text-bochas-azul uppercase tracking-wide font-semibold"
                    >
                      Vos
                    </span>
                  </div>
                  <div class="text-gray-400 text-xs sm:hidden">
                    {{ user.email }}
                  </div>
                  <div class="mt-0.5 text-xs text-bochas-text-muted">
                    {{ roleLabel(user.role, user) }}
                  </div>
                </div>
              </div>
            </td>
            <td class="px-4 py-3 text-gray-300 text-sm hidden sm:table-cell">
              {{ user.email }}
            </td>
            <td class="px-4 py-3 hidden lg:table-cell">
              <button
                type="button"
                class="inline-flex max-w-[13rem] items-center rounded-full border border-white/10 bg-white/[0.06] px-3 py-1.5 text-left text-xs font-semibold text-white transition-colors hover:border-bochas-azul/60 hover:bg-bochas-azul/15"
                @click="openEditModal(user)"
              >
                <span class="truncate">{{ roleLabel(user.role, user) }}</span>
              </button>
            </td>
            <td class="px-4 py-3 hidden md:table-cell">
              <span
                v-if="user.mustChangePassword"
                class="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 inline-flex items-center gap-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Pendiente
              </span>
              <span
                v-else
                class="text-xs px-2 py-1 rounded bg-green-500/20 text-green-300 inline-flex items-center gap-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Activo
              </span>
            </td>
            <td class="px-4 py-3 text-right">
              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  @click="openEditModal(user)"
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                  title="Editar usuario"
                 aria-label="Editar"><IconFbochas name="edit" :size="16" /></button>
                <button
                  v-if="user.id !== currentUserId"
                  type="button"
                  @click="openDeleteModal(user)"
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                  title="Eliminar usuario"
                 aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
              </div>
            </td>
          </tr>
          <tr v-if="filteredUsers.length === 0">
            <td colspan="5" class="px-4 py-12 text-center text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-12 h-12 mx-auto mb-3 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p class="text-sm">
                {{
                  search
                    ? "Ningún usuario coincide con la búsqueda."
                    : "No hay usuarios administradores todavía."
                }}
              </p>
              <button
                v-if="search"
                @click="search = ''"
                class="mt-3 text-bochas-azul hover:text-white text-sm underline"
              >
                Limpiar búsqueda
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-if="users.length > 0"
        class="px-4 py-3 border-t border-bochas-card-border text-xs text-bochas-text-muted"
      >
        Mostrando {{ filteredUsers.length }} de {{ users.length }} usuario{{
          users.length === 1 ? "" : "s"
        }}
      </div>
    </div>

    <!-- Modal: Editar usuario (con opción de resetear contraseña) -->
    <Teleport to="body">
      <div
        v-if="editingUser"
        @click="closeEditModal"
        class="mobile-modal-overlay fixed inset-0 z-50 flex justify-center bg-black/80 sm:items-center sm:p-4"
      >
        <div
          v-bottom-sheet="closeEditModal"
          @click.stop
          class="mobile-bottom-sheet w-full max-w-md overflow-y-auto rounded-t-2xl border border-bochas-card-border bg-bochas-bg p-6 sm:rounded-2xl"
        >
          <div class="flex items-center gap-3 mb-6">
            <div
              class="w-12 h-12 rounded-full bg-degradado-bochas flex items-center justify-center text-white font-semibold flex-shrink-0"
            >
              {{ editingUser ? initials(editingUser) : "" }}
            </div>
            <div>
              <h3 class="text-lg font-semibold text-white">
                {{ editingUser?.firstname }} {{ editingUser?.lastname }}
              </h3>
              <p class="text-sm text-bochas-text-muted">
                {{ editingUser?.email }}
              </p>
            </div>
          </div>

          <form @submit.prevent="onSaveEdit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Nombre</label
              >
              <input
                v-model="editForm.firstname"
                type="text"
                required
                class="w-full px-4 py-2.5 bg-bochas-card-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-azul"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Apellido</label
              >
              <input
                v-model="editForm.lastname"
                type="text"
                required
                class="w-full px-4 py-2.5 bg-bochas-card-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-azul"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Email</label
              >
              <input
                v-model="editForm.email"
                type="email"
                required
                class="w-full px-4 py-2.5 bg-bochas-card-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-azul"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Rol</label
              >
              <div class="grid gap-2 sm:grid-cols-3">
                <button
                  v-for="role in roleOptions"
                  :key="role.value"
                  type="button"
                  class="rounded-lg border px-3 py-3 text-left text-sm font-semibold transition-colors"
                  :class="
                    editForm.role === role.value
                      ? 'border-bochas-azul bg-bochas-azul/20 text-white'
                      : 'border-gray-700 bg-bochas-card-bg text-gray-300 hover:border-gray-500 hover:text-white'
                  "
                  @click="editForm.role = role.value"
                >
                  {{ role.label }}
                </button>
              </div>
            </div>
            <label
              v-if="editForm.role === ROLES.BOCHAS_ADMIN"
              class="flex items-center gap-3 rounded-lg border border-gray-700 bg-bochas-card-bg px-4 py-3 text-sm text-gray-300"
            >
              <input
                v-model="editForm.isDeveloper"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-600 bg-bochas-bg text-bochas-rojo focus:ring-bochas-rojo"
              />
              <span>Administrador general</span>
            </label>
            <div
              v-if="editNeedsAsociacion"
              class="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2"
                  >Asociación</label
                >
                <select
                  v-model.number="editForm.asociacionId"
                  required
                  class="w-full px-4 py-2.5 bg-bochas-card-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-azul"
                >
                  <option :value="null" disabled>Seleccionar asociación</option>
                  <option
                    v-for="asociación in asociaciones"
                    :key="asociación.id"
                    :value="asociación.id"
                  >
                    {{ asociación.name }}
                  </option>
                </select>
              </div>
              <div v-if="editForm.role === ROLES.CLUB_ADMIN">
                <label class="block text-sm font-medium text-gray-300 mb-2"
                  >Club</label
                >
                <select
                  v-model.number="editForm.clubId"
                  required
                  class="w-full px-4 py-2.5 bg-bochas-card-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-azul"
                >
                  <option :value="null" disabled>Seleccionar club</option>
                  <option
                    v-for="club in editClubesFiltrados"
                    :key="club.id"
                    :value="club.id"
                  >
                    {{ club.name }}
                  </option>
                </select>
              </div>
            </div>

            <!-- Resetear contraseña -->
            <div
              v-if="editingUser?.id !== currentUserId"
              class="border-t border-bochas-card-border pt-4"
            >
              <button
                type="button"
                @click="openResetPassword"
                class="text-bochas-azul hover:text-white text-sm underline"
              >
                Resetear contraseña de este usuario
              </button>
              <p class="text-xs text-bochas-text-muted mt-1">
                Ingresá una contraseña temporal nueva (mínimo 6 caracteres).
              </p>
            </div>

            <div
              v-if="editError"
              class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
            >
              {{ editError }}
            </div>

            <div class="flex gap-3 justify-end pt-2">
              <button
                type="button"
                @click="closeEditModal"
                class="px-4 py-2 text-gray-400 hover:text-white"
                :disabled="editLoading"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn-bochas-azul flex items-center gap-2"
                :class="{ 'opacity-60 cursor-not-allowed': editLoading }"
                :disabled="editLoading"
              >
                <span v-if="!editLoading">Guardar cambios</span>
                <span v-else>Guardando...</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modal: Confirmar eliminación -->
    <Teleport to="body">
      <div
        v-if="deletingUser"
        @click="closeDeleteModal"
        class="mobile-modal-overlay fixed inset-0 z-50 flex justify-center bg-black/80 sm:items-center sm:p-4"
      >
        <div
          v-bottom-sheet="closeDeleteModal"
          @click.stop
          class="mobile-bottom-sheet w-full max-w-md rounded-t-2xl border border-bochas-card-border bg-bochas-bg p-6 sm:rounded-2xl"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-10 h-10 rounded-full bg-bochas-rojo/20 flex items-center justify-center text-bochas-rojo flex-shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-white">¿Eliminar usuario?</h3>
          </div>
          <p class="text-bochas-text-muted text-sm mb-4">
            Estás por eliminar a
            <strong class="text-white"
              >{{ deletingUser?.firstname }}
              {{ deletingUser?.lastname }}</strong
            >
            (<span class="text-gray-300">{{ deletingUser?.email }}</span
            >).<br />
            Está acción no se puede deshacer.
          </p>
          <div
            v-if="deleteError"
            class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3 mb-4"
          >
            {{ deleteError }}
          </div>
          <div class="flex gap-3 justify-end">
            <button
              type="button"
              @click="closeDeleteModal"
              class="px-4 py-2 text-gray-400 hover:text-white"
              :disabled="deleteLoading"
            >
              Cancelar
            </button>
            <button
              type="button"
              @click="onConfirmDelete"
              class="px-4 py-2 bg-bochas-rojo hover:bg-bochas-rojo/80 text-white rounded-lg flex items-center gap-2"
              :class="{ 'opacity-60 cursor-not-allowed': deleteLoading }"
              :disabled="deleteLoading"
            >
              <span v-if="!deleteLoading">Sí, eliminar</span>
              <span v-else>Eliminando...</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal: Resetear contraseña (dentro del modal de edición) -->
    <Teleport to="body">
      <div
        v-if="resettingPassword"
        @click="closeResetPassword"
        class="mobile-modal-overlay fixed inset-0 z-[60] flex justify-center bg-black/80 sm:items-center sm:p-4"
      >
        <div
          v-bottom-sheet="closeResetPassword"
          @click.stop
          class="mobile-bottom-sheet w-full max-w-md rounded-t-2xl border border-bochas-card-border bg-bochas-bg p-6 sm:rounded-2xl"
        >
          <h3 class="text-lg font-semibold text-white mb-2">
            Resetear contraseña
          </h3>
          <p class="text-bochas-text-muted text-sm mb-4">
            Cambiar la contraseña de
            <strong class="text-white"
              >{{ editingUser?.firstname }} {{ editingUser?.lastname }}</strong
            >. El usuario deberá cambiarla al próximo ingreso.
          </p>
          <form @submit.prevent="onConfirmResetPassword" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2"
                >Nueva contraseña del usuario (mín. 6 caracteres)</label
              >
              <input
                v-model="resetForm.newPassword"
                type="text"
                required
                minlength="6"
                class="w-full px-4 py-2.5 bg-bochas-card-bg border border-gray-700 rounded-lg text-white focus:outline-none focus:border-bochas-azul"
                placeholder="nueva123"
              />
            </div>
            <div
              v-if="resetError"
              class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 text-sm rounded-lg px-4 py-3"
            >
              {{ resetError }}
            </div>
            <div class="flex gap-3 justify-end">
              <button
                type="button"
                @click="closeResetPassword"
                class="px-4 py-2 text-gray-400 hover:text-white"
                :disabled="resetLoading"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn-bochas-azul flex items-center gap-2"
                :class="{ 'opacity-60 cursor-not-allowed': resetLoading }"
                :disabled="resetLoading"
              >
                <span v-if="!resetLoading">Cambiar contraseña</span>
                <span v-else>Cambiando...</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { ref, computed, onMounted, watch } from "vue";
import {
  listarUsuarios,
  crearUsuario,
  editarUsuario,
  eliminarUsuario,
  cambiarPassword,
  getStoredUser,
} from "@/services/auth/authService";
import { formatDate } from "@/utils/date";
import { closeBottomSheet } from "@/directives/bottomSheet";
import { listarAsociacionesAdmin } from "@/services/asociaciones/asociacionesService";
import { listarClubesAdmin } from "@/services/clubes/clubesService";
import { ROLES, roleLabel } from "@/utils/roles";

const users = ref([]);
const asociaciones = ref([]);
const clubes = ref([]);
const loading = ref(true);
const search = ref("");
const currentUserId = computed(() => getStoredUser()?.id);

const showForm = ref(false);
const formLoading = ref(false);
const formError = ref("");
const showTempPassword = ref(false);
const form = ref({
  firstname: "",
  lastname: "",
  email: "",
  password: "",
  role: ROLES.BOCHAS_ADMIN,
  isDeveloper: false,
  asociacionId: null,
  clubId: null,
});

const roleOptions = [
  ROLES.BOCHAS_ADMIN,
  ROLES.Asociación_ADMIN,
  ROLES.CLUB_ADMIN,
].map((role) => ({ value: role, label: roleLabel(role) }));

const needsAsociacion = computed(() =>
  [ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN].includes(form.value.role),
);

const clubesFiltrados = computed(() => {
  if (!form.value.asociacionId) return [];
  return clubes.value.filter(
    (club) => club.asociacionId === form.value.asociacionId,
  );
});

watch(
  () => form.value.role,
  (role) => {
    if (![ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN].includes(role)) {
      form.value.asociacionId = null;
    }
    if (role !== ROLES.CLUB_ADMIN) {
      form.value.clubId = null;
    }
    if (role !== ROLES.BOCHAS_ADMIN) {
      form.value.isDeveloper = false;
    }
  },
);

watch(
  () => form.value.asociacionId,
  () => {
    form.value.clubId = null;
  },
);

const editingUser = ref(null);
const editForm = ref({
  firstname: "",
  lastname: "",
  email: "",
  role: ROLES.BOCHAS_ADMIN,
  isDeveloper: false,
  asociacionId: null,
  clubId: null,
});
const editLoading = ref(false);
const editError = ref("");

const editNeedsAsociacion = computed(() =>
  [ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN].includes(editForm.value.role),
);

const editClubesFiltrados = computed(() => {
  if (!editForm.value.asociacionId) return [];
  return clubes.value.filter(
    (club) => club.asociacionId === editForm.value.asociacionId,
  );
});

watch(
  () => editForm.value.role,
  (role) => {
    if (![ROLES.Asociación_ADMIN, ROLES.CLUB_ADMIN].includes(role)) {
      editForm.value.asociacionId = null;
    }
    if (role !== ROLES.CLUB_ADMIN) {
      editForm.value.clubId = null;
    }
    if (role !== ROLES.BOCHAS_ADMIN) {
      editForm.value.isDeveloper = false;
    }
  },
);

watch(
  () => editForm.value.asociacionId,
  () => {
    editForm.value.clubId = null;
  },
);

const deletingUser = ref(null);
const deleteLoading = ref(false);
const deleteError = ref("");

const resettingPassword = ref(false);
const resetLoading = ref(false);
const resetError = ref("");
const resetForm = ref({ newPassword: "" });

const toast = ref({ show: false, type: "success", message: "" });

function showToast(type, message) {
  toast.value = { show: true, type, message };
  setTimeout(() => {
    toast.value.show = false;
  }, 3000);
}

const filteredUsers = computed(() => {
  const q = search.value.toLowerCase().trim();
  if (!q) return users.value;
  return users.value.filter(
    (u) =>
      (u.firstname && u.firstname.toLowerCase().includes(q)) ||
      (u.lastname && u.lastname.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q)),
  );
});

function initials(user) {
  if (!user) return "?";
  const f = (user.firstname || "")[0] || "";
  const l = (user.lastname || "")[0] || "";
  return (f + l).toUpperCase() || "?";
}

function resetCreateForm() {
  form.value = {
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    role: ROLES.BOCHAS_ADMIN,
    isDeveloper: false,
    asociacionId: null,
    clubId: null,
  };
  formError.value = "";
  showTempPassword.value = false;
}

async function load() {
  loading.value = true;
  try {
    const [data, asociacionesData, clubesData] = await Promise.all([
      listarUsuarios(),
      listarAsociacionesAdmin(),
      listarClubesAdmin(),
    ]);
    users.value = data.users || [];
    asociaciones.value = asociacionesData.items || [];
    clubes.value = clubesData.items || [];
  } catch (err) {
    console.error("Error cargando usuarios:", err);
    showToast("error", err.message || "Error al cargar usuarios.");
  } finally {
    loading.value = false;
  }
}

async function onCreate() {
  formError.value = "";
  formLoading.value = true;
  try {
    const payload = { ...form.value };
    if (payload.role !== ROLES.BOCHAS_ADMIN) {
      payload.isDeveloper = false;
    }
    if (!needsAsociacion.value) {
      payload.asociacionId = null;
      payload.clubId = null;
    } else if (payload.role !== ROLES.CLUB_ADMIN) {
      payload.clubId = null;
    }
    await crearUsuario(payload);
    showToast(
      "success",
      `Usuario ${form.value.email} creado. Contraseña temporal: "${form.value.password}"`,
    );
    resetCreateForm();
    showForm.value = false;
    await load();
  } catch (err) {
    formError.value = err.message;
  } finally {
    formLoading.value = false;
  }
}

function openEditModal(user) {
  editingUser.value = user;
  editForm.value = {
    firstname: user.firstname || "",
    lastname: user.lastname || "",
    email: user.email || "",
    role: user.role || ROLES.BOCHAS_ADMIN,
    isDeveloper: Boolean(user.isDeveloper),
    asociacionId: user.asociacionId || null,
    clubId: user.clubId || null,
  };
  editError.value = "";
}
function closeEditModal() {
  if (editLoading.value) return;
  closeBottomSheet(() => {
    editingUser.value = null;
    resettingPassword.value = false;
  });
}
async function onSaveEdit() {
  editError.value = "";
  editLoading.value = true;
  try {
    const payload = { ...editForm.value };
    if (payload.role !== ROLES.BOCHAS_ADMIN) {
      payload.isDeveloper = false;
    }
    if (!editNeedsAsociacion.value) {
      payload.asociacionId = null;
      payload.clubId = null;
    } else if (payload.role !== ROLES.CLUB_ADMIN) {
      payload.clubId = null;
    }
    await editarUsuario({ userId: editingUser.value.id, ...payload });
    showToast("success", "Usuario actualizado correctamente.");
    editLoading.value = false;
    closeEditModal();
    await load();
  } catch (err) {
    editError.value = err.message;
  } finally {
    editLoading.value = false;
  }
}

function openDeleteModal(user) {
  deletingUser.value = user;
  deleteError.value = "";
}
function closeDeleteModal() {
  if (deleteLoading.value) return;
  closeBottomSheet(() => {
    deletingUser.value = null;
  });
}
async function onConfirmDelete() {
  deleteError.value = "";
  deleteLoading.value = true;
  try {
    await eliminarUsuario(deletingUser.value.id);
    showToast("success", `Usuario ${deletingUser.value.email} eliminado.`);
    closeDeleteModal();
    await load();
  } catch (err) {
    deleteError.value = err.message;
  } finally {
    deleteLoading.value = false;
  }
}

function openResetPassword() {
  resettingPassword.value = true;
  resetError.value = "";
  resetForm.value = { newPassword: "" };
}
function closeResetPassword() {
  if (resetLoading.value) return;
  closeBottomSheet(() => {
    resettingPassword.value = false;
    resetForm.value = { newPassword: "" };
  });
}
async function onConfirmResetPassword() {
  resetError.value = "";
  resetLoading.value = true;
  try {
    await cambiarPassword({
      userId: editingUser.value.id,
      newPassword: resetForm.value.newPassword,
      isAdminReset: true,
    });
    showToast(
      "success",
      `Contraseña reseteada. Comunicásela a ${editingUser.value.firstname}.`,
    );
    closeResetPassword();
  } catch (err) {
    resetError.value = err.message;
  } finally {
    resetLoading.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

select {
  color-scheme: dark;
}

select option {
  background-color: var(--bochas-bg);
  color: white;
}
</style>
